import React, { useEffect, useState, useCallback } from "react";
import MetricCards from '../components/admin/analytics/MetricCards';
import SalesChart from '../components/admin/analytics/SalesChart';
import TopProduct from '../components/admin/analytics/TopProduct';
import ProductSalesTable from '../components/admin/analytics/ProductSalesTable';
import BranchSelector from '../components/admin/analytics/BranchSelector';
import EmptyState from '../components/CommonComponents/EmptyState';
import MonthPicker from '../components/CommonComponents/MonthPicker';
import { BarChart3, CalendarDays, TrendingUp } from 'lucide-react';
import { useTheme } from "../components/CommonComponents/ThemeProvider";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

const getManilaDateKey = (date = new Date()) => date.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const getMonthKey = (date = new Date()) => getManilaDateKey(date).slice(0, 7);

const formatMonthLabel = (monthKey) => {
    const [rawYear, rawScope] = String(monthKey || "").split("-");
    if (/^\d{4}$/.test(rawYear) && rawScope === "all") return `All ${rawYear}`;
    const [year, month] = String(monthKey || "").split("-").map(Number);
    if (!year || !month) return "Selected Month";
    return new Date(year, month - 1, 1).toLocaleDateString("en-PH", {
        month: "long",
        year: "numeric",
    });
};

const parseDateKey = (dateKey) => {
    const [year, month, day] = String(dateKey || "").slice(0, 10).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const AnalyticsSkeleton = () => (
    <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
        <div className="mb-8 flex items-center justify-between">
            <div className="h-10 w-56 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            <div className="h-10 w-64 rounded-xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
        </div>

        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-5">
            {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} className="rounded-xl bg-white p-4 shadow">
                    <div className="mx-auto h-7 w-24 rounded-full bg-slate-200" />
                    <div className="mx-auto mt-3 h-3 w-14 rounded-full bg-slate-100" />
                </div>
            ))}
        </div>

        <div className="mb-8 flex flex-col gap-6">
            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 flex items-center justify-between">
                    <div className="h-5 w-36 rounded-full bg-slate-200" />
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-24 rounded-lg bg-slate-100" />
                        <div className="h-9 w-20 rounded-lg bg-slate-100" />
                        <div className="h-9 w-9 rounded-lg bg-slate-100" />
                    </div>
                </div>
                <div className="h-80 rounded-2xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
                    <div className="flex h-full items-end gap-3">
                        {[48, 72, 58, 86, 54, 68, 78, 60, 74, 52, 80, 64].map((height, idx) => (
                            <div key={idx} className="flex flex-1 items-end">
                                <div
                                    className="w-full rounded-t-2xl bg-gradient-to-t from-indigo-200 via-indigo-100 to-slate-100"
                                    style={{ height: `${height}%` }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 h-5 w-40 rounded-full bg-slate-200" />
                <div className="h-72 rounded-2xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-4">
                    <div className="flex h-full items-end gap-4">
                        {[72, 55, 88, 64, 49, 77, 58, 69].map((height, idx) => (
                            <div key={idx} className="flex flex-1 items-end">
                                <div
                                    className="w-full rounded-t-2xl bg-gradient-to-t from-sky-200 via-sky-100 to-slate-100"
                                    style={{ height: `${height}%` }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl bg-white p-6 shadow">
                <div className="mb-4 h-5 w-44 rounded-full bg-slate-200" />
                <div className="space-y-3">
                    {Array.from({ length: 8 }).map((_, idx) => (
                        <div key={idx} className="grid grid-cols-3 gap-4 border-b pb-3">
                            <div className="h-3.5 rounded-full bg-slate-200" />
                            <div className="h-3.5 rounded-full bg-slate-100" />
                            <div className="h-3.5 rounded-full bg-slate-100" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const Analytics = () => {
    const { currentTheme } = useTheme();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [branchId, setBranchId] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(getMonthKey());

    // Fetch analytics data
    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

            const params = new URLSearchParams();
            if (customerId) params.set("customer_id", customerId);
            if (branchId) params.set("branch_id", branchId);
            if (selectedMonth) params.set("month", selectedMonth);
            const queryString = params.toString();
            const url = queryString ? `${API_URL}?${queryString}` : API_URL;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setAnalytics(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [branchId, selectedMonth]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const sortByDateAsc = (data) => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            const da = parseDateKey(a.date);
            const db = parseDateKey(b.date);
            if (!da || !db) return 0;
            return da - db;
        });
    };

    const selectedMonthLabel = formatMonthLabel(selectedMonth);
    const overview = {
        today: Number(analytics?.today?.totalSales || 0),
        month: Number(analytics?.total_sales || 0),
        orders: Number(analytics?.total_orders || 0),
        avg: Number(analytics?.avg_order_value || 0),
    };

    const salesByDayFiltered = sortByDateAsc(analytics?.salesByDay || []);
    const salesByDayLabels = salesByDayFiltered.map(d => d.date);
    const salesByDayData = salesByDayFiltered.map(d => parseFloat(d.totalSales));

    // Prepare chart data for top products (limit to 10 for chart)
    const top10 = (analytics?.topProducts ?? []).slice(0, 10);
    const topProductsLabels = top10.map(p => p.product || p.product?.name || p.product_id);
    const topProductsData = top10.map(p => parseInt(p.totalQty));
    const topProductsChart = {
        labels: topProductsLabels,
        datasets: [
            {
                label: 'Top 10 Products',
                data: topProductsData,
                backgroundColor: 'rgba(54, 162, 235, 0.6)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
            },
        ],
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-3">
            <section className="mb-4 rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                            <TrendingUp className="h-4 w-4" />
                            Sales Intelligence
                        </div>
                        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">Sales Analytics</h1>
                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                            Review sales movement, branch performance, and product demand from one workspace.
                        </p>
                    </div>
                    <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto">
                        <BranchSelector value={branchId} onChange={setBranchId} />
                        <div className="flex w-full flex-col gap-2 sm:w-auto">
                            <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                <CalendarDays className="h-3.5 w-3.5" />
                                Month Scope
                            </label>
                            <div className="min-w-[240px] flex-1">
                                <MonthPicker
                                    value={selectedMonth}
                                    onChange={(nextMonth) => setSelectedMonth(nextMonth || getMonthKey())}
                                    themeColor={currentTheme.color}
                                    ariaLabel="Select analytics month"
                                    includeAll
                                />
                            </div>
                            <span className="text-xs text-slate-500">Reports scoped to {selectedMonthLabel}</span>
                        </div>
                    </div>
                </div>
            </section>
            {error && (
                <EmptyState
                    icon={BarChart3}
                    title="Failed to Load Analytics"
                    message="Check your internet connection and try again."
                    onRetry={fetchAnalytics}
                    retryLabel="Retry"
                />
            )}
            {!error && loading && <AnalyticsSkeleton />}
            {!error && !loading && analytics && (
                <>
                    {/* Metrics Card Row */}
                    <MetricCards
                        overview={overview}
                        periodLabel={selectedMonthLabel}
                        topProductsCount={(analytics?.topProducts || []).length}
                    />

                    {/* Stacked Full-Width Graphs: Sales and Top Products */}
                    <div className="flex flex-col gap-4 pb-8">
                        <SalesChart
                            salesByDayLabels={salesByDayLabels}
                            salesByDayData={salesByDayData}
                            analytics={analytics}
                            selectedMonth={selectedMonth}
                            selectedMonthLabel={selectedMonthLabel}
                        />
                            <TopProduct topProductsChart={topProductsChart} />
                            <ProductSalesTable products={analytics?.topProducts ?? []} />
                    </div>
                </>
            )}
        </div>
    );
};

export default Analytics;
